import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { MarketplacePropertyCategory_CertaintyPanelClickEvent_Row as PanelRowKind } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { IconShieldLock, IconTruck } from '@tabler/icons-react';
import { useObservability } from 'helpers/observability';

import { FreeCancellationIcon } from '../../FreeCancellationIcon';

const GUARANTEE_BADGE_SRC =
  'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764882992132-81635.png?preset=square-128&service=cosmos-cache-friendly&dpr=2';

const PROTECTIONS: Array<{ icon: ReactNode; title: string; description: string }> = [
  {
    icon: <IconTruck size={20} />,
    title: 'Early Move-In',
    description: 'Move in up to two weeks before closing, at no extra cost.',
  },
  {
    icon: <IconShieldLock size={20} />,
    title: '100-Day Warranty',
    description: 'We cover repairs to major systems for your first 100 days.',
  },
  {
    icon: <FreeCancellationIcon size={20} />,
    title: 'Free Cancellation',
    description: 'Change your mind within 14 days and walk away, no fee.',
  },
];

type GuaranteeCardProps = {
  addressToken: string;
  showMortgageCta: boolean;
};

export const GuaranteeCard = ({ addressToken, showMortgageCta }: GuaranteeCardProps) => {
  const { trackJourneyEvent } = useObservability();

  const onMortgageClick = () => {
    trackJourneyEvent('marketplaceProperty', 'certaintyPanelClick', {
      addressUuid: addressToken,
      row: PanelRowKind.GUARANTEE,
      target: 'mortgage',
    });
  };

  return (
    <Col
      as="section"
      aria-label="The Opendoor Guarantee"
      className="overflow-hidden rounded-2xl border border-od-border-primary bg-od-bg-primary"
    >
      <Row className="items-center gap-4 bg-od-bg-secondary px-5 py-5">
        <Image src={GUARANTEE_BADGE_SRC} alt="Opendoor Guarantee" width={44} height={44} />
        <Col className="gap-0.5">
          <Text as="h3" className="text-label-lg text-od-content-primary">
            The Opendoor Guarantee
          </Text>
          <Text as="p" className="text-body-sm text-od-content-secondary">
            Three protections, included with every home.
          </Text>
        </Col>
      </Row>
      {PROTECTIONS.map(({ icon, title, description }) => (
        <Row key={title} className="items-start gap-4 border-t border-od-border-primary px-5 py-4">
          <Box className="flex size-9 shrink-0 items-center justify-center rounded-full bg-od-bg-secondary text-od-content-primary">
            {icon}
          </Box>
          <Col className="gap-0.5">
            <Text as="span" className="text-label-md text-od-content-primary">
              {title}
            </Text>
            <Text as="p" className="text-body-sm text-od-content-secondary">
              {description}
            </Text>
          </Col>
        </Row>
      ))}
      {showMortgageCta && (
        <Row className="border-t border-od-border-primary px-5 py-4">
          <Button
            analyticsName="cosmos-marketplace-pdp-certainty-panel-mortgage"
            data-analytics-name="cosmos-marketplace-pdp-certainty-panel-mortgage"
            aria-label="Explore financing"
            variant="secondary"
            size="sm"
            onClick={onMortgageClick}
            href="/home-loans"
            target="_blank"
            rel="noopener"
          >
            Explore financing
          </Button>
        </Row>
      )}
    </Col>
  );
};
